import * as t from './types';
import * as r from './registry';
import * as c from './client';
import { RetirementConfig } from '../fin/retirement_config.ts';
import { RetirementState } from '../fin/retirement_state.ts';
import { simulate_multiple_histories } from '../fin/retirement.ts';
import { MultipleHistory } from '../fin/retirement';
import * as math from '../fin/math';

export function register(): void {
  r.registerService(
    t.WorkerAction.MontecarloRetirement,
    function (input: t.TaskInput): Promise<string> {
      const config = RetirementConfig.deserialize(input.serializedParameters);

      const pre_retirement_rs: RetirementState = new RetirementState(
        config.person.simulation_start_year,
        config.person,
        config.initialAssets,
        config.market,
        [],
      );

      console.assert(
        config.market.taxed_growth_rate.stddev > 0.01 &&
        config.market.untaxed_growth_rate.stddev > 0.01,
        'MontecarloRetirement got insufficient volatility of return in config.',
      );

      const history = simulate_multiple_histories(
        pre_retirement_rs,
        config.strategy,
        [],  // default flow strategies,
        input.reportProgress,
        1000,  // number of simulations
      );

      return Promise.resolve(history.serialize());
    },
  );

  r.registerService(
    t.WorkerAction.ParallelMontecarloRetirement,
    async function (input: t.TaskInput): Promise<string> {
      const config = typeof(input.serializedParameters) === 'string' ?
        RetirementConfig.deserialize(input.serializedParameters) :
        input.serializedParameters;

      console.assert(
        config.market.taxed_growth_rate.stddev > 0.01 &&
        config.market.untaxed_growth_rate.stddev > 0.01,
        'ParallelMontecarloRetirement got insufficient volatility of return in config.',
      );

      const NUM_SHARDS = 4;

      console.log(`ParallelMontecarloRetirement starting ${NUM_SHARDS} shards.`);

      class Progress {
        // shard number to completion ratio map...
        readonly state = new Map<number, number>();
        readonly parentListener: (completionRatio: number)=>void;

        constructor (
          listener: (completionRatio: number)=>void
        ) {
          this.parentListener = listener;
        }

        reportProgress(shardNumber: number, completionRatio: number) {
          this.state.set(shardNumber, completionRatio);
          this.parentListener(this.completionRatio);
        }

        get completionRatio(): number {
          return math.mean([...this.state.values()]);
        }
      };

      const progressTracker = new Progress(input.reportProgress);

      const results = [...Array(NUM_SHARDS).keys()].map(
        async function (shardNumber): Promise<MultipleHistory> {
          const child = c.startTask(
            t.WorkerAction.MontecarloRetirement,
            JSON.stringify(config.serialize()),
            (shardCompletionRatio: number) => {
              progressTracker.reportProgress(shardNumber, shardCompletionRatio);
            },
          );

          const resultString = await child.resultPromise;
          const mh = MultipleHistory.deserialize(resultString);
          return mh;
        }
      );

      console.log(`ParallelMontecarloRetirement started ${NUM_SHARDS} shards.`);

      const historiess = (await Promise.allSettled(results))
        .filter(r => r.status === 'fulfilled')
        .map(r => r.value)

      console.log(`ParallelMontecarloRetirement shards all complete (or failed)`);

      const merged = historiess.reduce((a,b) => a.merge(b));
      const mergedSerialized = merged.serialize();
      return mergedSerialized;

      console.log(`ParallelMontecarloRetirement returning`);
    },
  );
}